import { ChangeEvent, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';
import usersService from '../../../../services/usersService';
import formatCnpj from '../../../../utils/formatCnpj';
import formatCpf from '../../../../utils/formatCpf';
import isCnpjValid from '../../../../utils/isCnpjValid';
import isCpfvalid from '../../../../utils/isCpfValid';

interface UseAddCollaboratorInterface {
  loadUsers: (currentPage: number) => void;
  currentPage: number;
  authenticationType: string;
  companyProductType: string;
}

export default function useAddCollaborator({
  loadUsers,
  currentPage,
  authenticationType,
  companyProductType,
}: UseAddCollaboratorInterface) {
  const [isAdding, setIsAdding] = useState(false);
  const [modalCreateShow, setModalCreateShow] = useState(false);
  const [collaboratorId, setCollaboratorId] = useState('');
  const [selectedCompanyForm, setSelectedCompanyForm] = useState<{ value: string | null, label: string }>({ value: null, label: 'Selecione uma empresa associada' });
  const [productType, setProductType] = useState<'default' | 'taxImmunity'>('default');

  const { apiCall } = useApiCall();

  const isCpf = authenticationType === 'cpf';

  function handleCollaboratorIdChange(event: ChangeEvent<HTMLInputElement>) {
    if (isCpf) {
      const formattedCpf = formatCpf(event.target.value);
      setCollaboratorId(formattedCpf);

      const hasMoreThanFourteenDigits = formattedCpf.length > 14;
      if (hasMoreThanFourteenDigits) {
        const formattedCnpj = formatCnpj(event.target.value);
        setCollaboratorId(formattedCnpj);
      }

      return;
    }
    setCollaboratorId(event.target.value);
  }

  function handleCreateModalShow() {
    setModalCreateShow(true);
  }

  function handleCreateModalClose() {
    setModalCreateShow(false);
  }

  function handleCompanyFormChange(company: { value: string | null, label: string }) {
    setSelectedCompanyForm(company);
  }

  const addCollaborator = useCallback(async () => {
    if (isCpf) {
      if (!isCpfvalid(collaboratorId) && !isCnpjValid(collaboratorId)) {
        toast.error('CPF inválido');
        return;
      }
    }

    await apiCall({
      apiToCall: usersService.createMultiUsers,
      reqBody: JSON.stringify({
        list: [{ id: String(collaboratorId).toLowerCase(), associated_company: selectedCompanyForm.value, productType: companyProductType === 'all' ? productType : companyProductType }],
      }),
      onStartLoad: () => {
        setModalCreateShow(false);
        setIsAdding(true);
      },
      onEndLoad: () => {
        setIsAdding(false);
        setCollaboratorId('');
      },
      actionAfterResponse: (apiResponse) => {
        if (!apiResponse.success) {
          setIsAdding(false);
          toast.error('Não foi possível adicionar o colaborador');
          return;
        }
        loadUsers(currentPage);
        toast.success('Colaborador adicionado com sucesso');
      },
      catchMessage: 'Não foi possível adicionar o colaborador'
    });
  }, [isCpf, apiCall, collaboratorId, selectedCompanyForm.value, companyProductType, productType, loadUsers, currentPage]);

  return {
    isAdding,
    modalCreateShow,
    setModalCreateShow,
    collaboratorId,
    handleCollaboratorIdChange,
    addCollaborator,
    handleCreateModalShow,
    handleCreateModalClose,
    selectedCompanyForm,
    handleCompanyFormChange,
    productType,
    setProductType,
  };
}
